import React, { useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UrlCollectionSarang } from '../../../common/url-collection-sarang';
import * as accountManagementAction from "../../../redux/store/account/account.store";
import * as festivalCogManagementAction from "../../../redux/store-sarang/festival-cog/festival-cog-management.store";
import {
    getCookies, setCookies, getUserInfo, setCurrentFestival, removeCookies, DomainAdminSide, TokenKey, APIUrlDefault
} from "../../../utils/configuration";
import { permission } from "../../../common/constant";
import { ButtonGroup, Button } from "@mui/material";

/**
 * Senior FE Refactor Notes:
 * 1. Data-driven menu structure for easier maintenance.
 * 2. Strict permission engine mirroring original logic.
 * 3. Modular sub-components for better readability and re-rendering performance.
 * 4. Preservation of "hardcoded hidden" items (SheepMenu, ReportMenu).
 */

const UserProfile = ({ user, onLogout }) => (
    <div className="card-body">
        <div className="media chat-item">
            <img
                src={(!user || !user.avatar) ? "assets/images/avatar.png" : APIUrlDefault + user.avatar}
                alt="User Avatar"
                className="rounded-circle user-avatar-xl"
            />
            <div className="media-body text-center">
                <div className="chat-item-title">
                    <span className="chat-item-author">
                        <Link to={UrlCollectionSarang.Profile}>{user?.fullName}</Link>
                    </span>
                </div>
                <div className="chat-item-body">
                    <ButtonGroup variant="text" color="info" size="small">
                        <Button onClick={onLogout} title="Đăng xuất">
                            <i className="fas fa-power-off mr-2"></i>
                        </Button>
                        {user?.herdsmanId && (
                            <Button
                                title={user.chatTelegramId || "Kết nối telegram"}
                                color={user.chatTelegramId ? "info" : "error"}
                                onClick={() => !user.chatTelegramId && window.open("http://t.me/sarang_hn_bot", "_blank", "noreferrer")}
                            >
                                <i className="fab fa-telegram-plane mr-2"></i>
                            </Button>
                        )}
                        <Button>
                            <Link to={UrlCollectionSarang.Profile} title="Xem thông tin" className="text-primary">
                                <i className="fas fa-user mr-2"></i>
                            </Link>
                        </Button>
                        {user?.email === "sarang@gmail.com" && (
                            <Button color="info" title='Sự cố'>
                                <Link to={UrlCollectionSarang.Feedback}>
                                    <i className="fas fa-comment"></i>
                                </Link>
                            </Button>
                        )}
                    </ButtonGroup>
                </div>
            </div>
        </div>
    </div>
);

const NavItem = ({ label, path, currentPath }) => {
    const isActive = currentPath === path;
    return (
        <li className="nav-item">
            <Link className={`nav-link ${isActive ? 'active' : ''}`} to={path}>
                {label}
            </Link>
        </li>
    );
};

const NavGroup = ({ id, title, icon, activeGroup, children, currentPath }) => {
    if (!children || children.length === 0) return null;

    // Check if any child is active to expand the group
    const isExpanded = children.some(child => currentPath === child.path) || activeGroup;

    return (
        <li className="nav-item">
            <a
                className={`nav-link ${isExpanded ? 'active' : ''}`}
                href="#"
                data-toggle="collapse"
                aria-expanded={isExpanded}
                data-target={`#${id}`}
                aria-controls={id}
            >
                <i className={icon}></i>{title}
                <span className="badge badge-success">{children.length}</span>
            </a>
            <div id={id} className={`collapse submenu ${isExpanded ? 'show' : ''}`}>
                <ul className="nav flex-column">
                    {children.map((child, index) => (
                        <NavItem
                            key={index}
                            label={child.label}
                            path={child.path}
                            currentPath={currentPath}
                        />
                    ))}
                </ul>
            </div>
        </li>
    );
};

export default function SideBarSarang() {
    const [screenPermissions, setScreenPermissions] = useState([]);
    const [user] = useState(getUserInfo());
    const location = useLocation();
    const currentPath = location.pathname;

    useEffect(() => {
        const fetchPermissions = async () => {
            try {
                let cached = await getCookies("screenAllow");
                if (!cached) {
                    const res = await accountManagementAction.GetScreenAllow();
                    if (res?.content) {
                        cached = res.content;
                        setCookies("screenAllow", cached);
                    }
                }
                if (cached) setScreenPermissions(cached);
            } catch (err) { console.error(err); }
        };

        const fetchFestival = async () => {
            try {
                const res = await festivalCogManagementAction.GetCurrentFestivalCog();
                if (res?.content) setCurrentFestival(res.content);
            } catch (err) { }
        };

        fetchPermissions();
        fetchFestival();
    }, []);

    const onLogout = () => {
        ["isShowDialog", "isLockScreen", "screenAllow", TokenKey.token, TokenKey.refreshToken, TokenKey.returnUrl]
            .forEach(key => removeCookies(key));
        window.location.replace(DomainAdminSide + "/dang-nhap");
    };

    // --- Permission Check Helpers (Mirroring original logic precisely) ---
    const getVisibleMenu = useMemo(() => {
        const hasModule = (code) => screenPermissions.some(x => x.code === code);
        const getModuleChildren = (code) => screenPermissions.find(x => x.code === code)?.childrent || [];
        const hasChild = (moduleCode, childCode) => getModuleChildren(moduleCode).some(x => x.code === childCode);

        const menuStructure = [];

        // 1. Hoạt động Trundo (SWAT)
        if (hasModule("SWAT")) {
            const swatChildren = [];
            if (hasChild("SWAT", "HERDSMAN")) {
                swatChildren.push({ label: "Danh sách ACE", path: UrlCollectionSarang.HerdsmanList });
            }
            if (hasChild("SWAT", "HERDSMANWM")) {
                swatChildren.push({ label: "Danh sách ACE WM", path: UrlCollectionSarang.HerdsmanWMList });
            }
            if (swatChildren.length > 0) {
                menuStructure.push({
                    id: "submenu-swattrundo",
                    title: "Hoạt động Trundo",
                    icon: "fas fa-gem",
                    children: swatChildren,
                    activeGroup: currentPath === UrlCollectionSarang.HerdsmanList || currentPath === UrlCollectionSarang.HerdsmanWMList
                });
            }
        }

        // 2. Quản lý chiên (SHEEP) - HARDCODED HIDE as per original line 96
        const showSheepMenu = false; // Original code: setShowSheepMenu(false)
        if (showSheepMenu) {
            const sheepChildren = [];
            if (hasChild("SWAT", "CUSTOMER")) sheepChildren.push({ label: "Danh sách chiên", path: UrlCollectionSarang.CustomerList });
            if (hasChild("SWAT", "CUSTOMERNEEDHELP")) sheepChildren.push({ label: "Chiên cần bàn giao", path: UrlCollectionSarang.CustomerNeedHelpList });
            if (hasChild("SWAT", "CALENDAR")) sheepChildren.push({ label: "Lịch hẹn chia sẻ", path: UrlCollectionSarang.CalendarContact });
            if (hasChild("SWAT", "CALENDARNEEDHELP")) sheepChildren.push({ label: "Trợ giúp chia sẻ", path: UrlCollectionSarang.CalendarNeedHelp });

            if (sheepChildren.length > 0) {
                menuStructure.push({
                    id: "submenu-sheep",
                    title: "Quản lý chiên",
                    icon: "fas fa-fw fa-user-secret",
                    children: sheepChildren
                });
            }
        }

        // 3. Báo cáo thống kê (REPORT) - HARDCODED HIDE in original JSX (line 345)
        const showReportMenu = false; // Original code: false ? ...
        if (showReportMenu && hasModule("REPORT")) {
            const reportChildren = [];
            if (hasChild("REPORT", "SUMARYTRUNDOBYSWAT")) reportChildren.push({ label: "Tổng hợp theo T/Đồ", path: UrlCollectionSarang.SwatTrundoSumaryHerdsmanReport });
            if (hasChild("REPORT", "REPORTDETAILTRUNDOBYSWAT")) reportChildren.push({ label: "Chi tiết theo T/Đồ", path: UrlCollectionSarang.SwatTrundoSumaryByDateReport });
            if (hasChild("REPORT", "SUMARYTRUNDOBYDEPARTMENT")) reportChildren.push({ label: "Tổng hợp theo địa vực", path: UrlCollectionSarang.SwatTrundoSumaryDepartmentReport });
            if (hasChild("REPORT", "SUMARYTRUNDOBYZIONAREA")) reportChildren.push({ label: "Tổng hợp theo khu vực", path: UrlCollectionSarang.SwatTrundoSumaryZionReport });
            if (hasChild("REPORT", "SUMARYTRUNDOBYSWATWM")) reportChildren.push({ label: "Tổng hợp theo T.Đồ WM", path: UrlCollectionSarang.SwatTrundoSumaryHerdsmanWMTeamReport });
            if (hasChild("REPORT", "SUMARYTRUNDOBYWM")) reportChildren.push({ label: "Tổng hợp theo đội WM", path: UrlCollectionSarang.SwatTrundoSumaryWMTeamReport });

            if (reportChildren.length > 0) {
                menuStructure.push({
                    id: "submenu-6",
                    title: "Báo cáo thống kê",
                    icon: "fas fa-fw fa-chart-bar",
                    children: reportChildren
                });
            }
        }

        // 4. Danh mục (COMMON)
        if (hasModule("COMMON")) {
            const commonChildren = [];
            if (hasChild("COMMON", "DEPARTMENT")) commonChildren.push({ label: "Địa vực", path: UrlCollectionSarang.Department });
            if (hasChild("COMMON", "ZIONAREA")) commonChildren.push({ label: "Khu vực", path: UrlCollectionSarang.ZionArea });

            // Special rules for Common items
            if (user?.userRoles?.includes(permission.Admin) || hasChild("COMMON", "BRANCH")) {
                commonChildren.push({ label: "Chi hội", path: UrlCollectionSarang.Branch });
            }
            if (user?.email === "sarang@gmail.com") {
                commonChildren.push({ label: "Nhóm quyền", path: UrlCollectionSarang.RoleManagement });
                commonChildren.push({ label: "Người sử dụng", path: UrlCollectionSarang.UserManagement });
            }

            if (commonChildren.length > 0) {
                menuStructure.push({
                    id: "submenu-5",
                    title: "Danh mục",
                    icon: "fas fa-fw fa-cogs",
                    children: commonChildren,
                    activeGroup: currentPath === UrlCollectionSarang.ZionArea ||
                        currentPath === UrlCollectionSarang.Department ||
                        currentPath === UrlCollectionSarang.Branch ||
                        currentPath === UrlCollectionSarang.RoleManagement ||
                        currentPath === UrlCollectionSarang.UserManagement
                });
            }
        }

        // 5. Đặt cơm (CUISINE)
        if (hasModule("CUISINE")) {
            const cuisineChildren = [];
            if (hasChild("CUISINE", "CUISINEORDER")) cuisineChildren.push({ label: "Đăng ký suất ăn", path: UrlCollectionSarang.CuisineOrder });
            if (hasChild("CUISINE", "CUISINE_REPORT")) cuisineChildren.push({ label: "Thống kê", path: UrlCollectionSarang.DashboardCooking });

            if (cuisineChildren.length > 0) {
                menuStructure.push({
                    id: "submenu-2",
                    title: "Đặt cơm",
                    icon: "fas fa-fw fa-birthday-cake",
                    children: cuisineChildren,
                    activeGroup: currentPath === UrlCollectionSarang.CuisineOrder || currentPath === UrlCollectionSarang.DashboardCooking
                });
            }
        }

        // 6. Giao vận (TRANSPORT)
        if (hasModule("TRANSPORT")) {
            const transportChildren = [];
            if (hasChild("TRANSPORT", "TRANSPORTORDER")) transportChildren.push({ label: "Đơn hàng", path: UrlCollectionSarang.ReportActivityShipping });
            if (hasChild("TRANSPORT", "SEND_SHIPPER")) transportChildren.push({ label: "Giao Shipper", path: UrlCollectionSarang.SendCuisineToShipper });
            if (hasChild("TRANSPORT", "TRANSPORT_REPORT")) transportChildren.push({ label: "Thống kê", path: UrlCollectionSarang.DashboardShipping });

            if (transportChildren.length > 0) {
                menuStructure.push({
                    id: "submenu-4",
                    title: "Giao vận",
                    icon: "fas fa-fw fa-truck-moving",
                    children: transportChildren,
                    activeGroup: currentPath === UrlCollectionSarang.ReportActivityShipping ||
                        currentPath === UrlCollectionSarang.SendCuisineToShipper ||
                        currentPath === UrlCollectionSarang.DashboardShipping
                });
            }
        }

        return menuStructure;
    }, [screenPermissions, user, currentPath]);

    const isDashboardHome = currentPath === UrlCollectionSarang.DashBoardHome;

    return (
        <div className="nav-left-sidebar sidebar-dark">
            <div className="menu-list">
                <nav className="navbar navbar-expand-lg navbar-light">
                    <button className="navbar-toggler" type="button" data-toggle="collapse" data-target="#navbarNav">
                        <span className="navbar-toggler-icon"></span>
                    </button>
                    <div className="collapse navbar-collapse" id="navbarNav">
                        <ul className="navbar-nav flex-column">
                            <UserProfile user={user} onLogout={onLogout} />

                            <li className="nav-item">
                                <Link className={`nav-link ${isDashboardHome ? 'active' : ''}`} to={UrlCollectionSarang.DashBoardHome}>
                                    <i className="fas fa-fw fa-home"></i>Trang chủ
                                </Link>
                            </li>

                            {getVisibleMenu.map((group, index) => (
                                <NavGroup
                                    key={index}
                                    id={group.id}
                                    title={group.title}
                                    icon={group.icon}
                                    children={group.children}
                                    currentPath={currentPath}
                                    activeGroup={group.activeGroup}
                                />
                            ))}

                            <li className="nav-item">
                                <a className="nav-link" href="#" onClick={(e) => {
                                    e.preventDefault();
                                    window.open("https://drive.google.com/file/d/11aBcTt8vF2HBeklITDBGjx0_zCQ_8bS2/view?usp=sharing", "TutorialTab");
                                }}>
                                    <i className="fas fa-fw fa-question-circle"></i>Hướng dẫn
                                </a>
                            </li>
                        </ul>
                    </div>
                </nav>
            </div>
        </div>
    );
}